import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { TrendingUp, Award, Users, Building, CreditCard } from 'lucide-react';
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip } from 'recharts';
import FirstTimeChecklist from '../components/FirstTimeChecklist';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PartnerWorkbench({ user }) {
  const [opportunities, setOpportunities] = useState([]);
  const [active, setActive] = useState({ portfolios: [], kpis: {} });
  const [leaderboard, setLeaderboard] = useState({ category: 'most_active', rankings: [] });
  const [profile, setProfile] = useState(null);
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [oppRes, activeRes, leaderRes, profileRes, memberRes] = await Promise.all([
        axios.get(`${API}/partner/opportunities`, { headers }),
        axios.get(`${API}/partner/active`, { headers }),
        axios.get(`${API}/partner/leaderboard?category=most_active`, { headers }),
        axios.get(`${API}/partner/profile`, { headers }),
        axios.get(`${API}/partner/membership`, { headers })
      ]);

      setOpportunities(oppRes.data.opportunities);
      setActive({ portfolios: activeRes.data.portfolios, kpis: activeRes.data.kpis });
      setLeaderboard({ category: 'most_active', rankings: leaderRes.data.rankings });
      setProfile(profileRes.data);
      setMembership(memberRes.data);
    } catch (error) {
      console.error('Error fetching partner data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpportunityResponse = async (oppId, action, comment = '') => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/partner/opportunities/${oppId}/respond`, 
        { action, comment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Opportunity ${action}ed successfully`);
      fetchAllData(); // Refresh
    } catch (error) {
      toast.error('Action failed');
    }
  };

  const switchLeaderboard = async (category) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/partner/leaderboard?category=${category}`, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLeaderboard({ category, rankings: res.data.rankings });
    } catch (error) {
      toast.error('Failed to load leaderboard');
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: '#FFFFFF' }}><div className="text-xl" style={{ color: '#0B0B0B' }}>Loading...</div></div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6" style={{ color: '#0B0B0B', fontFamily: 'Space Grotesk' }}>Partner Workbench</h1>

      <FirstTimeChecklist role="broker" userId={user?.id} />

      <Tabs defaultValue="opportunities" className="space-y-6">
        <TabsList className="bg-accent/20 border border-primary/20">
          <TabsTrigger value="opportunities" data-testid="broker-nav-opportunities" data-tour-id="partner-opportunities-tab">Opportunities</TabsTrigger>
          <TabsTrigger value="active" data-testid="broker-nav-portfolios" data-tour-id="partner-active-tab">Active</TabsTrigger>
          <TabsTrigger value="leaderboard" data-testid="broker-nav-directory" data-tour-id="partner-leaderboard-tab">Leaderboard</TabsTrigger>
          <TabsTrigger value="profile" data-testid="broker-nav-disputes" data-tour-id="partner-directory-profile">Profile</TabsTrigger>
          <TabsTrigger value="membership" data-testid="broker-nav-payouts" data-tour-id="partner-membership-btn">Membership</TabsTrigger>
        </TabsList>

        <TabsContent value="opportunities">
          <div className="space-y-4">
            {opportunities.map((opp, idx) => (
              <Card key={opp.id} data-testid={idx === 0 ? 'broker-opportunity-0' : `broker-opportunity-${idx}`} className="glass-card border-primary/20">
                <CardHeader>
                  <CardTitle style={{ color: '#4B9DFF' }}>{opp.portfolio_name}</CardTitle>
                  <p className="text-sm" style={{ color: '#5B6472' }}>Creator: {opp.creator_name}</p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div><p className="text-sm" style={{ color: '#5B6472' }}>Category</p><p style={{ color: '#0B0B0B' }}>{opp.category}</p></div>
                    <div><p className="text-sm" style={{ color: '#5B6472' }}>Units</p><p style={{ color: '#0B0B0B' }}>{opp.total_units}</p></div>
                    <div><p className="text-sm" style={{ color: '#5B6472' }}>Kickback</p><p style={{ color: '#47FFB0' }}>{opp.kickback_percent}%</p></div>
                    <div><p className="text-sm" style={{ color: '#5B6472' }}>Est. Revenue</p><p style={{ color: '#47FFB0' }}>${opp.estimated_revenue.toLocaleString()}</p></div>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => handleOpportunityResponse(opp.id, 'accept')} style={{ background: '#47FFB0', color: '#0D0D0D' }}>Accept</Button>
                    <Button onClick={() => handleOpportunityResponse(opp.id, 'decline')} style={{ background: 'rgba(255, 107, 107, 0.2)', color: '#FF6B6B' }}>Decline</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="active">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card className="glass-card border-primary/20">
              <CardContent className="pt-6">
                <p className="text-sm" style={{ color: '#5B6472' }}>Total Portfolios</p>
                <p className="text-3xl font-bold" style={{ color: '#4B9DFF' }}>{active.kpis.total_portfolios}</p>
              </CardContent>
            </Card>
            <Card className="glass-card border-primary/20">
              <CardContent className="pt-6">
                <p className="text-sm" style={{ color: '#5B6472' }}>Avg YTD ROI</p>
                <p className="text-3xl font-bold" style={{ color: '#47FFB0' }}>{active.kpis.avg_ytd_roi?.toFixed(1)}%</p>
              </CardContent>
            </Card>
            <Card className="glass-card border-primary/20">
              <CardContent className="pt-6">
                <p className="text-sm" style={{ color: '#5B6472' }}>Total Sales</p>
                <p className="text-3xl font-bold" style={{ color: '#47FFB0' }}>${active.kpis.total_sales?.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card className="glass-card border-primary/20">
              <CardContent className="pt-6">
                <p className="text-sm" style={{ color: '#5B6472' }}>Payout Cycles</p>
                <p className="text-3xl font-bold" style={{ color: '#4B9DFF' }}>{active.kpis.total_payout_cycles}</p>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-4">
            {active.portfolios.map((p) => (
              <Card key={p.id} className="glass-card border-primary/20">
                <CardHeader>
                  <CardTitle style={{ color: '#4B9DFF' }}>{p.name}</CardTitle>
                  <p className="text-sm" style={{ color: '#5B6472' }}>Creator: {p.creator}</p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div><p className="text-sm" style={{ color: '#5B6472' }}>YTD ROI</p><p style={{ color: '#47FFB0', fontWeight: '600' }}>{p.ytd_roi}%</p></div>
                    <div><p className="text-sm" style={{ color: '#5B6472' }}>Sales</p><p style={{ color: '#0B0B0B' }}>${p.total_sales.toLocaleString()}</p></div>
                    <div><p className="text-sm" style={{ color: '#5B6472' }}>Threshold</p><p style={{ color: p.threshold_status === 'met' ? '#47FFB0' : '#FFC107' }}>{p.threshold_percent}% ({p.threshold_status})</p></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="leaderboard">
          <div className="flex gap-2 mb-6">
            <Button onClick={() => switchLeaderboard('most_active')} style={{ background: leaderboard.category === 'most_active' ? '#4B9DFF' : 'rgba(75, 157, 255, 0.2)', color: '#FFF' }}>Most Active</Button>
            <Button onClick={() => switchLeaderboard('best_roi')} style={{ background: leaderboard.category === 'best_roi' ? '#4B9DFF' : 'rgba(75, 157, 255, 0.2)', color: '#FFF' }}>Best ROI</Button>
            <Button onClick={() => switchLeaderboard('fastest_payout')} style={{ background: leaderboard.category === 'fastest_payout' ? '#4B9DFF' : 'rgba(75, 157, 255, 0.2)', color: '#FFF' }}>Fastest Payout</Button>
          </div>

          <Card className="glass-card border-primary/20">
            <CardHeader><CardTitle style={{ color: '#4B9DFF' }}>Rankings - {leaderboard.category.replace(/_/g, ' ')}</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {leaderboard.rankings.map((r) => (
                  <div key={r.rank} className={`p-3 rounded ${r.is_you ? 'border-2 border-yellow-500' : ''}`} style={{ background: r.is_you ? 'rgba(255, 193, 7, 0.1)' : 'rgba(75, 157, 255, 0.05)' }}>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl font-bold" style={{ color: r.rank === 1 ? '#FFD700' : '#4B9DFF' }}>#{r.rank}</span>
                        <div>
                          <p style={{ color: '#0B0B0B', fontWeight: '600' }}>{r.partner} {r.is_you && '(You)'}</p>
                          <p className="text-sm" style={{ color: '#5B6472' }}>Score: {r.score}</p>
                        </div>
                      </div>
                      {r.portfolios && <p style={{ color: '#47FFB0' }}>{r.portfolios} portfolios</p>}
                      {r.avg_roi && <p style={{ color: '#47FFB0' }}>{r.avg_roi}% ROI</p>}
                      {r.avg_days && <p style={{ color: '#47FFB0' }}>{r.avg_days} days</p>}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="profile">
          {profile && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="glass-card border-primary/20">
                <CardHeader><CardTitle style={{ color: '#4B9DFF' }}>Profile Info</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div><p className="text-sm" style={{ color: '#5B6472' }}>Name</p><p style={{ color: '#0B0B0B' }}>{profile.name}</p></div>
                    <div><p className="text-sm" style={{ color: '#5B6472' }}>Specialties</p><div className="flex gap-2 flex-wrap">{profile.specialties.map((s, i) => <span key={i} className="text-xs px-2 py-1 rounded" style={{ background: 'rgba(75, 157, 255, 0.1)', color: '#4B9DFF' }}>{s}</span>)}</div></div>
                    <div><p className="text-sm" style={{ color: '#5B6472' }}>Licenses</p>{profile.license_ids.map((l, i) => <p key={i} className="text-sm" style={{ color: '#0B0B0B' }}>{l}</p>)}</div>
                  </div>
                </CardContent>
              </Card>
              <Card className="glass-card border-primary/20">
                <CardHeader><CardTitle style={{ color: '#4B9DFF' }}>Performance</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div><p className="text-sm" style={{ color: '#5B6472' }}>Success Rate</p><p className="text-2xl font-bold" style={{ color: '#47FFB0' }}>{profile.success_rate}%</p></div>
                    <div><p className="text-sm" style={{ color: '#5B6472' }}>Portfolios Managed</p><p style={{ color: '#0B0B0B' }}>{profile.portfolios_managed}</p></div>
                    <div><p className="text-sm" style={{ color: '#5B6472' }}>Avg ROI</p><p style={{ color: '#47FFB0' }}>{profile.avg_roi}%</p></div>
                    <div><p className="text-sm" style={{ color: '#5B6472' }}>Total Payouts</p><p style={{ color: '#47FFB0' }}>${profile.total_payouts.toLocaleString()}</p></div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="membership">
          {membership && (
            <div className="space-y-6">
              <Card className="glass-card border-primary/20">
                <CardHeader><CardTitle style={{ color: '#4B9DFF' }}>Current Plan</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="text-2xl font-bold" style={{ color: '#0B0B0B' }}>{membership.current_plan.tier}</p>
                      <p className="text-lg" style={{ color: '#47FFB0' }}>${membership.current_plan.price}/month</p>
                      <p className="text-sm mt-2" style={{ color: '#5B6472' }}>Next billing: {membership.current_plan.next_billing_date}</p>
                    </div>
                    <span className="px-3 py-1 rounded" style={{ background: 'rgba(71, 255, 176, 0.1)', color: '#47FFB0' }}>{membership.current_plan.status}</span>
                  </div>
                  <div className="space-y-1">
                    {membership.current_plan.features.map((f, i) => <p key={i} className="text-sm" style={{ color: '#0B0B0B' }}>✓ {f}</p>)}
                  </div>
                </CardContent>
              </Card>

              <Card className="glass-card border-primary/20">
                <CardHeader><CardTitle style={{ color: '#4B9DFF' }}>Billing History</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {membership.billing_history.map((b, i) => (
                      <div key={i} className="flex justify-between p-2 rounded" style={{ background: 'rgba(75, 157, 255, 0.05)' }}>
                        <span style={{ color: '#0B0B0B' }}>{b.date}</span>
                        <span style={{ color: '#47FFB0' }}>${b.amount}</span>
                        <span style={{ color: '#5B6472' }}>{b.status}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
